import { useState, useMemo } from 'react';
import { useI18n } from '../i18n';
import './ActivityCalendar.css';

interface DayData {
  solo: number;
  multiplayer: number;
  daily: number;
}

type Mode = 'solo' | 'multiplayer' | 'daily';

interface Props {
  activity: Record<string, DayData>;
  modes?: Set<Mode>;
  onModesChange?: (modes: Set<Mode>) => void;
}

// Ay/gün adları Intl.DateTimeFormat ile locale'e göre üretilir (N-dil desteği).
const LOCALE_TAG: Record<string, string> = {
  tr: 'tr-TR', en: 'en-US', nl: 'nl-NL', fr: 'fr-FR', de: 'de-DE',
  it: 'it-IT', pl: 'pl-PL', ru: 'ru-RU', es: 'es-ES',
};

function getShortMonthNames(locale: string): string[] {
  const tag = LOCALE_TAG[locale] ?? 'en-US';
  const fmt = new Intl.DateTimeFormat(tag, { month: 'short' });
  return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2024, i, 1)));
}

export function ActivityCalendar({ activity, modes: modesProp, onModesChange }: Props) {
  const { t, locale } = useI18n();
  const ac = t.activityCalendar as any;
  const localeTag = LOCALE_TAG[locale] ?? 'en-US';
  const MODE_LABELS: Record<Mode, string> = {
    solo: ac.modeSolo, multiplayer: ac.modeMultiplayer, daily: ac.modeDaily,
  };
  const MONTH_NAMES = useMemo(() => getShortMonthNames(locale), [locale]);
  // Pazartesi=0 başlangıçlı 7 günlük kısaltma sırası (haftanın görünen etiketleri)
  const DAY_LABELS = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(localeTag, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)));
  }, [localeTag]);

  const [internalModes, setInternalModes] = useState<Set<Mode>>(new Set(['solo', 'multiplayer', 'daily']));
  const modes = modesProp ?? internalModes;
  const setModes = onModesChange ?? setInternalModes;
  const [tooltip, setTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);

  const currentYear = today.getFullYear();

  // Activity datasındaki yılları + mevcut yıl + önceki yıl
  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear, currentYear - 1]);
    Object.keys(activity).forEach(k => years.add(parseInt(k.slice(0, 4))));
    return Array.from(years).sort((a, b) => b - a); // yeniden eskiye
  }, [activity, currentYear]);

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const toggleMode = (m: Mode) => {
    const next = new Set(modes);
    if (next.has(m)) {
      if (next.size === 1) return;
      next.delete(m);
    } else {
      next.add(m);
    }
    setModes(next);
  };

  const weeks = useMemo(() => {
    // Başlangıç: seçili yılın 1 Ocak'ı
    const wStart = new Date(selectedYear, 0, 1);
    // Bitiş: her yıl için 31 Aralık (gelecek günler soluk gösterilir)
    const wEnd = new Date(selectedYear, 11, 31);

    const dow = (wStart.getDay() + 6) % 7;
    const cursor = new Date(wStart);
    cursor.setDate(cursor.getDate() - dow);

    const allWeeks: (Date | null)[][] = [];
    while (cursor <= wEnd) {
      const week: (Date | null)[] = [];
      for (let d = 0; d < 7; d++) {
        const day = new Date(cursor);
        day.setDate(cursor.getDate() + d);
        // Pencere dışı hücreleri boş bırak
        week.push(day >= wStart && day <= wEnd ? day : null);
      }
      allWeeks.push(week);
      cursor.setDate(cursor.getDate() + 7);
    }
    return allWeeks;
  }, [selectedYear]);

  const getCount = (date: Date): number => {
    const key = date.toISOString().slice(0, 10);
    const d = activity[key];
    if (!d) return 0;
    let total = 0;
    if (modes.has('solo')) total += d.solo;
    if (modes.has('multiplayer')) total += d.multiplayer;
    if (modes.has('daily')) total += d.daily;
    return total;
  };

  const getLevel = (count: number): 0 | 1 | 2 | 3 | 4 => {
    if (count === 0) return 0;
    if (count === 1) return 1;
    if (count <= 3) return 2;
    if (count <= 6) return 3;
    return 4;
  };

  const monthLabels = useMemo(() => {
    const labels: { label: string; col: number }[] = [];
    let lastMonth = -1;
    weeks.forEach((week, colIdx) => {
      const firstReal = week.find(d => d !== null);
      if (firstReal && firstReal.getMonth() !== lastMonth) {
        lastMonth = firstReal.getMonth();
        labels.push({ label: MONTH_NAMES[lastMonth], col: colIdx });
      }
    });
    return labels;
  }, [weeks, MONTH_NAMES]);

  const yearTotal = useMemo(() => {
    return Object.entries(activity).reduce((sum, [key, d]) => {
      if (!key.startsWith(String(selectedYear))) return sum;
      let t = 0;
      if (modes.has('solo')) t += d.solo;
      if (modes.has('multiplayer')) t += d.multiplayer;
      if (modes.has('daily')) t += d.daily;
      return sum + t;
    }, 0);
  }, [activity, modes, selectedYear]);

  return (
    <div className="activity-calendar">
      <div className="activity-header">
        <span className="activity-title">
          {(() => {
            const tpl = ac.yearGames.replace('{year}', String(selectedYear));
            const [before, after] = tpl.split('{n}');
            return <>{before}<strong>{yearTotal}</strong>{after}</>;
          })()}
        </span>
        <div className="activity-modes">
          {(['solo', 'multiplayer', 'daily'] as Mode[]).map(m => (
            <button
              key={m}
              className={`activity-mode-btn ${modes.has(m) ? 'active' : ''}`}
              onClick={() => toggleMode(m)}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>
      </div>

      {/* Yıl seçici */}
      <div className="activity-year-tabs">
        {availableYears.map(y => (
          <button
            key={y}
            className={`activity-year-btn ${selectedYear === y ? 'active' : ''}`}
            onClick={() => setSelectedYear(y)}
          >
            {y}
          </button>
        ))}
      </div>

      <div className="activity-grid-wrap">
        <div
          className="activity-month-row"
          style={{ gridTemplateColumns: `repeat(${weeks.length}, 13px)` }}
        >
          {monthLabels.map(({ label, col }) => (
            <span
              key={`${label}-${col}`}
              className="activity-month-label"
              style={{ gridColumnStart: col + 1 }}
            >
              {label}
            </span>
          ))}
        </div>

        <div className="activity-body">
          <div className="activity-day-labels">
            <span>{DAY_LABELS[0]}</span>
            <span></span>
            <span>{DAY_LABELS[2]}</span>
            <span></span>
            <span>{DAY_LABELS[4]}</span>
            <span></span>
            <span></span>
          </div>
          <div className="activity-grid">
            {weeks.map((week, wi) => (
              <div key={wi} className="activity-week">
                {week.map((day, di) => {
                  if (!day) return <div key={di} className="activity-cell activity-cell--empty" />;
                  const future = day > today;
                  const count = future ? 0 : getCount(day);
                  const level = getLevel(count);
                  const key = day.toISOString().slice(0, 10);
                  return (
                    <div
                      key={di}
                      className={`activity-cell activity-cell--l${level}${future ? ' activity-cell--future' : ''}`}
                      onMouseEnter={e => {
                        const rect = (e.target as HTMLElement).getBoundingClientRect();
                        const d = activity[key];
                        const parts: string[] = [];
                        if (d) {
                          if (modes.has('solo') && d.solo) parts.push(ac.soloCount.replace('{n}', String(d.solo)));
                          if (modes.has('multiplayer') && d.multiplayer) parts.push(ac.multiplayerCount.replace('{n}', String(d.multiplayer)));
                          if (modes.has('daily') && d.daily) parts.push(ac.dailyCount.replace('{n}', String(d.daily)));
                        }
                        const dateLabel = day.toLocaleDateString(localeTag, { day: 'numeric', month: 'long', year: 'numeric' });
                        setTooltip({
                          text: count === 0
                            ? ac.noGames.replace('{date}', dateLabel)
                            : ac.tooltipPrefix.replace('{date}', dateLabel).replace('{parts}', parts.join(', ')),
                          x: rect.left + rect.width / 2,
                          y: rect.top - 8,
                        });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="activity-legend">
          <span>{ac.legendLow}</span>
          {[0, 1, 2, 3, 4].map(l => (
            <div key={l} className={`activity-cell activity-cell--l${l}`} />
          ))}
          <span>{ac.legendHigh}</span>
        </div>
      </div>

      {tooltip && (
        <div
          className="activity-tooltip"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}
